// @hssna/family-web 认证状态 (Zustand)
// 规格: SPEC §4.1 API 客户端架构 / §7 安全合规 / §8.1 family-web 功能规格
//
// 职责:
//   - 持有 AuthClient 单例 (main.tsx 注入)
//   - 持有当前 tokens / 解析后的 JwtPayload / 角色
//   - init: 从 TokenStore 恢复已登录会话 (localStorage 持久化)
//   - setTokens: 登录/MFA 成功后写入
//   - logout: 调用 AuthClient.logout 并清空状态
//   - refreshUser: 从当前 access_token 重新解析 payload

import { create } from 'zustand';
import type { AuthClient, JwtPayload, Role } from '@hssna/ts-shared';
import { decodeJwt } from '@hssna/ts-shared';
import type { AuthTokens } from '@hssna/ui-kit';

/** 合法角色集合 (SPEC §7, 10 角色, SSOT: data/ddl/0020_seed_rbac_roles.up.sql) */
const VALID_ROLES: readonly Role[] = [
  'family_head',
  'family_member',
  'family_guardian',
  'practitioner',
  'specialist',
  'nurse',
  'ops_admin',
  'ops_operator',
  'ems_dispatcher',
  'system_service',
];

/** 从 JwtPayload 中提取首个合法角色
 * 兼容两种 claim 格式:
 *   - roles: ["family-member", ...]  (数组, 历史格式)
 *   - role: "family-member"          (单数, s1-identity 实际签发格式)
 * 并自动规范化连字符 → 下划线 (family-member → family_member)
 */
function extractRole(payload: JwtPayload | null): Role | null {
  if (!payload) return null;

  // 收集候选角色 (合并 roles 数组和 role 单数)
  const candidates: string[] = [];
  if (payload.roles && Array.isArray(payload.roles)) {
    candidates.push(...payload.roles);
  }
  const singleRole = (payload as { role?: unknown }).role;
  if (typeof singleRole === 'string') {
    candidates.push(singleRole);
  }

  // 规范化并匹配
  for (const r of candidates) {
    const normalized = r.replace(/-/g, '_');
    if (VALID_ROLES.includes(normalized as Role)) {
      return normalized as Role;
    }
  }
  return null;
}

export interface AuthState {
  /** S1 认证客户端 (main.tsx 注入) */
  authClient: AuthClient | null;
  /** 当前 token 集合 (登录直通 / MFA 验证后) */
  tokens: AuthTokens | null;
  /** 从 access_token 解析出的 payload */
  user: JwtPayload | null;
  /** 当前用户角色 (从 user.roles 提取) */
  role: Role | null;
  /** 是否已认证 */
  isAuthenticated: boolean;
  /** 初始化中 (恢复会话期间) */
  isInitializing: boolean;

  /** 注入 AuthClient 并尝试从 TokenStore 恢复会话 */
  init: (authClient: AuthClient) => Promise<void>;
  /** 登录/MFA 成功后写入 tokens 并解析 user/role */
  setTokens: (tokens: AuthTokens) => void;
  /** 登出: 调用 AuthClient.logout, 清空状态 */
  logout: () => Promise<void>;
  /** 从当前 access_token 重新解析 user/role */
  refreshUser: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  authClient: null,
  tokens: null,
  user: null,
  role: null,
  isAuthenticated: false,
  isInitializing: true,

  init: async (authClient) => {
    set({ authClient, isInitializing: true });
    try {
      // 获取有效 access_token (access_token 过期时自动用 refresh_token 刷新)
      const validToken = await authClient.getValidAccessToken();
      if (validToken) {
        const payload = decodeJwt(validToken);
        if (payload) {
          // refresh 后 refresh_token 可能已更新, 从 tokenStore 重新读取
          const refreshToken = await authClient.getTokenStore().getRefreshToken();
          set({
            tokens: {
              access_token: validToken,
              refresh_token: refreshToken ?? '',
              mfa_completed: true,
            } as AuthTokens,
            user: payload,
            role: extractRole(payload),
            isAuthenticated: true,
          });
        }
      }
    } catch {
      // refresh 失败 (refresh_token 也已失效), 保持未登录状态
    } finally {
      set({ isInitializing: false });
    }
  },

  setTokens: (tokens) => {
    const payload = decodeJwt(tokens.access_token);
    set({
      tokens,
      user: payload,
      role: extractRole(payload),
      isAuthenticated: true,
    });
  },

  logout: async () => {
    const { authClient } = get();
    if (authClient) {
      try {
        await authClient.logout();
      } catch {
        // 登出网络错误不阻塞本地清空
      }
    }
    set({
      tokens: null,
      user: null,
      role: null,
      isAuthenticated: false,
    });
  },

  refreshUser: () => {
    const { tokens } = get();
    if (!tokens) {
      return;
    }
    const payload = decodeJwt(tokens.access_token);
    set({ user: payload, role: extractRole(payload) });
  },
}));
